import _ from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {type RunsLinePlotConfig} from '../types';

export const DEBOUNCE_DELAY_MS = 500;

export type UseDebouncedCombinedLimitArgs = {
  config: RunsLinePlotConfig;
  updateConfig: (config: RunsLinePlotConfig) => void;
  maxRunsLimit: number;
  /** Current metricLimit from context/config (used for sync) */
  metricLimit: number;
  /** Current limit from context/config (used for sync) */
  limit: number;
  /** Optional callback fired when debounced config update commits */
  onCommit?: (values: {metricLimit?: number; limit?: number}) => void;
};

export type UseDebouncedCombinedLimitReturn = {
  /** Local metricLimit value for immediate UI display */
  localMetricLimit: number;
  /** Local limit value for immediate UI display */
  localLimit: number;
  /** Handler for metricLimit changes - updates local state immediately, debounces config update */
  handleMetricLimitChange: (value: number) => void;
  /** Handler for limit changes - updates local state immediately, debounces config update */
  handleRunLimitChange: (value: number) => void;
  /** Reset handler - cancels pending updates and clears both values */
  handleReset: () => void;
};

/**
 * Calculate the constrained values for limit and metricLimit.
 * Enforces: metricLimit * limit <= maxRunsLimit
 *
 * When the constraint is violated, the non-target field is adjusted down.
 * If the target value is so large that the other field would need to be < 1,
 * the target is capped at maxRunsLimit and the other field is set to 1.
 */
export function calculateConstrainedValues(
  targetValue: number,
  otherValue: number,
  maxRunsLimit: number,
  target: 'metricLimit' | 'limit'
): {metricLimit: number; limit: number} {
  const isSafe = targetValue * otherValue <= maxRunsLimit;

  if (isSafe) {
    return target === 'metricLimit'
      ? {metricLimit: targetValue, limit: otherValue}
      : {metricLimit: otherValue, limit: targetValue};
  }

  const adjustedOther = Math.floor(maxRunsLimit / targetValue);

  if (adjustedOther < 1) {
    // Target is too large - cap it and set other to 1
    return target === 'metricLimit'
      ? {metricLimit: maxRunsLimit, limit: 1}
      : {metricLimit: 1, limit: maxRunsLimit};
  }

  return target === 'metricLimit'
    ? {metricLimit: targetValue, limit: adjustedOther}
    : {metricLimit: adjustedOther, limit: targetValue};
}

/**
 * Hook for managing debounced updates to the combined metric/run limit controls.
 *
 * Provides immediate UI feedback via local state while debouncing the actual
 * config updates to prevent a cascade of requests.
 *
 * Enforces the constraint: metricLimit * limit <= maxRunsLimit
 * When the constraint is violated, the non-target field is adjusted IMMEDIATELY
 * in the UI, and the debounced config update includes both adjusted values.
 */
export function useDebouncedCombinedLimit({
  config,
  updateConfig,
  maxRunsLimit,
  metricLimit,
  limit,
  onCommit,
}: UseDebouncedCombinedLimitArgs): UseDebouncedCombinedLimitReturn {
  // Local state for immediate UI feedback
  const [localMetricLimit, setLocalMetricLimit] = useState(metricLimit);
  const [localLimit, setLocalLimit] = useState(limit);

  // Use refs to always have latest values in the debounced function
  const configRef = useRef(config);
  configRef.current = config;

  // Use ref for onCommit to avoid recreating debounced function when callback changes
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const dispatchConfigUpdate = useCallback(
    (
      newVals: {metricLimit: number; limit: number},
      prevVals: {metricLimit: number; limit: number}
    ) => {
      updateConfig({
        ...configRef.current,
        metricLimit: newVals.metricLimit,
        limit: newVals.limit,
      });

      // Build delta with only changed properties
      const delta: {metricLimit?: number; limit?: number} = {};
      if (newVals.metricLimit !== prevVals.metricLimit) {
        delta.metricLimit = newVals.metricLimit;
      }
      if (newVals.limit !== prevVals.limit) {
        delta.limit = newVals.limit;
      }

      if (Object.keys(delta).length > 0) {
        onCommitRef.current?.(delta);
      }
    },
    [updateConfig]
  );

  const debouncedDispatchConfigUpdate = useMemo(
    () => _.debounce(dispatchConfigUpdate, DEBOUNCE_DELAY_MS),
    [dispatchConfigUpdate]
  );

  // Sync local state when context values change (e.g., from reset button or external updates)
  useEffect(() => {
    setLocalMetricLimit(metricLimit);
    debouncedDispatchConfigUpdate.cancel();
  }, [debouncedDispatchConfigUpdate, metricLimit]);

  useEffect(() => {
    setLocalLimit(limit);
    debouncedDispatchConfigUpdate.cancel();
  }, [debouncedDispatchConfigUpdate, limit]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedDispatchConfigUpdate.cancel();
    };
  }, [debouncedDispatchConfigUpdate]);

  // We need refs for local values to avoid stale closures in handlers
  const localLimitRef = useRef(localLimit);
  localLimitRef.current = localLimit;

  const localMetricLimitRef = useRef(localMetricLimit);
  localMetricLimitRef.current = localMetricLimit;

  const handleMetricLimitChange = useCallback(
    (value: number) => {
      const prevMetricLimit = localMetricLimitRef.current;
      const prevLimit = localLimitRef.current;

      // Calculate constrained values immediately
      const {metricLimit: newMetricLimit, limit: newLimit} =
        calculateConstrainedValues(
          value,
          prevLimit,
          maxRunsLimit,
          'metricLimit'
        );

      // Update both local states immediately for instant UI feedback
      setLocalMetricLimit(newMetricLimit);
      setLocalLimit(newLimit);

      debouncedDispatchConfigUpdate(
        {metricLimit: newMetricLimit, limit: newLimit},
        {metricLimit: prevMetricLimit, limit: prevLimit}
      );
    },
    [debouncedDispatchConfigUpdate, maxRunsLimit]
  );

  const handleRunLimitChange = useCallback(
    (value: number) => {
      const prevMetricLimit = localMetricLimitRef.current;
      const prevLimit = localLimitRef.current;

      // Calculate constrained values immediately
      const {metricLimit: newMetricLimit, limit: newLimit} =
        calculateConstrainedValues(
          value,
          prevMetricLimit,
          maxRunsLimit,
          'limit'
        );

      // Update both local states immediately for instant UI feedback
      setLocalMetricLimit(newMetricLimit);
      setLocalLimit(newLimit);

      debouncedDispatchConfigUpdate(
        {metricLimit: newMetricLimit, limit: newLimit},
        {metricLimit: prevMetricLimit, limit: prevLimit}
      );
    },
    [debouncedDispatchConfigUpdate, maxRunsLimit]
  );

  const handleReset = useCallback(() => {
    // Cancel any pending debounced updates
    debouncedDispatchConfigUpdate.cancel();
    updateConfig({
      ...configRef.current,
      limit: undefined,
      metricLimit: undefined,
    });
  }, [debouncedDispatchConfigUpdate, updateConfig]);

  return {
    localMetricLimit,
    localLimit,
    handleMetricLimitChange,
    handleRunLimitChange,
    handleReset,
  };
}
